import PropTypes from 'prop-types'
import { useEffect } from 'react'
import styled, { css } from 'styled-components'
import { RichText } from 'prismic-reactjs'
import { fluidRange } from 'polished'

import { htmlSerializer } from '../../utils/htmlSerializer'

import Container from '../Container'
import Display from '../Display'
import Headline from '../Headline'
import Hero from '../Hero'
import Text from '../Text'

const ContactDetail = (props) => {
  const {
    eyebrow,
    headline,
    accessibility_supports,
    corporate_headquarters,
    emails,
    member_supports,
  } = props.data

  useEffect(() => {
    const accessibilityLink = document.querySelector('.accessibility-statement a')
    accessibilityLink?.setAttribute('target', '_blank')
  }, [])

  return (
    <>
      <Hero padding={'120px 20px'}>
        <Eyebrow size="eyebrow">{eyebrow}</Eyebrow>

        <HeroHeadline size="medium">{headline}</HeroHeadline>
      </Hero>

      <Container>
        <Row>
          <TypeText size="eyebrow">Email</TypeText>
          <Items email>
            {emails.map((email, index) => (
              <Item key={email.email}>
                <ItemHeadline>{email.type}</ItemHeadline>

                <Email href={`mailto:${ email.email }`} data-testid={`email-${ email.type.toLowerCase().replace(/\s/g, '-') }`}>
                  <EmailHeadline size="small">{email.email}</EmailHeadline>
                </Email>
              </Item>
            ))}
          </Items>
        </Row>

        <Row>
          <TypeText size="eyebrow">Member Support</TypeText>
          <Items>
            {member_supports.map((member_support, index) => (
              <Item key={member_support.type} data-testid={`member-support-${ member_support.type.toLowerCase().replace(/\s/g, '-') }`}>
                <ItemHeadline>{member_support.type}</ItemHeadline>

                {<RichText htmlSerializer={htmlSerializer} render={member_support.text} />}
              </Item>
            ))}
          </Items>
        </Row>

        <Row id="accessibility">
          <TypeText size="eyebrow">Accessibility Support</TypeText>

          <Items>
            {accessibility_supports.map((accessibility_support, index) => (
              <Item key={accessibility_support.type} data-testid={`accessibility-support-${ accessibility_support.type.toLowerCase().replace(/\s/g, '-') }`}>
                <ItemHeadline>{accessibility_support.type}</ItemHeadline>

                {<RichText htmlSerializer={htmlSerializer} render={accessibility_support.text} />}
              </Item>
            ))}
          </Items>
        </Row>

        <Row>
          <TypeText size="eyebrow">Corporate Headquarters</TypeText>

          <Items>
            {corporate_headquarters.map((corporate_headquarter, index) => (
              <Item key={`corporate-headquarters-item-${ index.toString() }`}>
                {corporate_headquarter.type && <ItemHeadline>{corporate_headquarter.type}</ItemHeadline>}

                {<RichText htmlSerializer={htmlSerializer} render={corporate_headquarter.text} />}
              </Item>
            ))}
          </Items>
        </Row>
      </Container>
    </>
  )
}

const ItemHeadline = styled(Headline)`
  margin: 0 0 18px;
`

const TypeText = styled(Text)`
  margin: 0 0 32px;
  text-transform: uppercase;
`

const Eyebrow = styled(Text)`
  margin: 0 0 32px;
  text-transform: uppercase;
`

const HeroHeadline = styled(Display)`
  margin: 0;

  @media screen and (max-width: ${ ({ theme }) => theme.breakpoints.xs1 }) {
    width: 330px;
    font-size: 50px;
  }
`

const linkStyles = css`
  font-family: ${ ({ theme }) => `${ theme.fonts.MabryProRegular }` };
  text-decoration: underline;

  &::after {
    content: none;
  }
`

const Row = styled.div`
  position: relative;
  padding-top: 60px;
  margin: 0 0 90px;
  border-top: 1px solid ${ ({ theme }) => theme.colors.gray[3] };

  @media screen and (min-width: ${ ({ theme }) => theme.breakpoints.sm }) {
    padding-top: 0;
    margin: 0 0 180px;
    border: none;
  }

  &:last-child {
    margin: 0;
  }

  &:first-child {
    padding-top: 0;
    border: none;
  }
`

const Items = styled.ul`
  display: grid;
  padding: 0;
  margin: 0;
  gap: 70px 30px;
  grid-template-columns: repeat(1, 1fr);

  @media screen and (min-width: ${ ({ theme }) => theme.breakpoints.xs1 }) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${ ({ theme }) => theme.breakpoints.sm }) {
    gap: ${ ({ email }) => (email ? '100px' : 0) } 50px;
    grid-template-columns: repeat(${ ({ email }) => (email ? '2' : '3') }, 1fr);
  }

  a {
    ${ linkStyles }
  }

  p:not([data-testid]) {
    font-family: ${ ({ theme }) => `${ theme.fonts.MabryProRegular }` };
    font-weight: 400;
    line-height: ${ ({ theme, size }) => theme.headline.small.lineHeight };
    letter-spacing: ${ ({ theme, size }) => `${ theme.headline.small.letterSpacing }em` };

    ${ ({ theme, size }) =>
    fluidRange(
      {
        prop: 'font-size',
        fromSize: `${ theme.headline.small.fontSizeMin }px`,
        toSize: `${ theme.headline.small.fontSizeMax }px`,
      },
      `${ theme.breakpoints.xs }`,
      `${ theme.breakpoints.xl }`,
    ) }
  }

  br {
    display: block;
    padding: 5px 0;
    content: '';
  }
`

const Item = styled.li`
  flex-flow: column;
  display: flex;
  width: 100%;
  max-width: 345px;

  > p {
    :nth-child(2) {
      margin-top: 0;
    }
  }
`

const Email = styled.a`
  ${ linkStyles }
`

const EmailHeadline = styled(Headline)`
  margin: 0;
  font-family: ${ ({ theme }) => `${ theme.fonts.MabryProRegular }` };
`

ContactDetail.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
}

export default ContactDetail
